<template>
  <div class="journey-report">
    <validation-observer ref="simpleRules">
      <b-form>
        <b-card
          no-body
          class="mb-1 shadow-sm border-0 filters-card"
        >
          <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="SlidersIcon"
                size="16"
                class="text-primary mr-50"
              />
              <h6 class="mb-0 font-weight-bolder">
                {{ $t('Filters') }}
              </h6>
            </div>
          </b-card-header>

          <b-card-body class="p-1">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                lg="3"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="date"
                  rules="required"
                >
                  <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                    <feather-icon
                      icon="CalendarIcon"
                      size="13"
                      class="mr-25 text-primary"
                    />
                    <span>{{ $t('Date') }}</span>
                    <span class="text-danger ml-25">*</span>
                  </label>
                  <b-input-group size="sm">
                    <b-input-group-prepend is-text>
                      <feather-icon
                        icon="CalendarIcon"
                        size="14"
                      />
                    </b-input-group-prepend>
                    <flat-pickr
                      v-model="date"
                      class="form-control"
                      :config="flatpickrDateConfig"
                      :placeholder="$t('Select...')"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>

              <b-col
                cols="12"
                sm="6"
                lg="3"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="startHour"
                  rules=""
                >
                  <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                    <feather-icon
                      icon="ClockIcon"
                      size="13"
                      class="mr-25 text-primary"
                    />
                    <span>{{ $t('Start Hour') }}</span>
                  </label>
                  <b-input-group size="sm">
                    <b-input-group-prepend is-text>
                      <feather-icon
                        icon="ClockIcon"
                        size="14"
                      />
                    </b-input-group-prepend>
                    <flat-pickr
                      v-model="startHour"
                      class="form-control"
                      :config="flatpickrConfig"
                      :placeholder="$t('Select...')"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>

              <b-col
                cols="12"
                sm="6"
                lg="3"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="endHour"
                  rules=""
                >
                  <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                    <feather-icon
                      icon="ClockIcon"
                      size="13"
                      class="mr-25 text-primary"
                    />
                    <span>{{ $t('End Hour') }}</span>
                  </label>
                  <b-input-group size="sm">
                    <b-input-group-prepend is-text>
                      <feather-icon
                        icon="ClockIcon"
                        size="14"
                      />
                    </b-input-group-prepend>
                    <flat-pickr
                      v-model="endHour"
                      class="form-control"
                      :config="flatpickrConfig"
                      :placeholder="$t('Select...')"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
            </b-row>

            <div class="d-flex align-items-center justify-content-between flex-wrap mt-50 pt-75 border-top">
              <div class="text-muted font-small-2 d-flex align-items-center mb-50 mb-md-0">
                <feather-icon
                  icon="InfoIcon"
                  size="14"
                  class="mr-50 text-primary"
                />
                <span>{{ $t('Default hours hint') }}</span>
              </div>
              <b-button
                variant="primary"
                size="sm"
                class="d-inline-flex align-items-center"
                :disabled="loadingJourney || loadingHistory"
                @click.prevent="validationForm"
              >
                <b-spinner
                  v-if="loadingJourney || loadingHistory"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('Apply Filters') }}</span>
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-form>
    </validation-observer>

    <b-row
      v-if="stops.length || rides.length"
      class="mb-1"
    >
      <b-col
        v-for="kpi in journeyKpis"
        :key="kpi.key"
        cols="6"
        xl="3"
        class="mb-1"
      >
        <b-card
          no-body
          class="journey-kpi shadow-sm border-0 h-100 p-1"
        >
          <div class="d-flex align-items-center justify-content-between">
            <div class="min-width-0">
              <small class="text-muted font-weight-bolder text-uppercase">{{ kpi.label }}</small>
              <h4
                class="mb-0 font-weight-bolder"
                :class="kpi.valueClass"
              >
                {{ kpi.value }}
                <small
                  v-if="kpi.unit"
                  class="journey-kpi-unit"
                >{{ kpi.unit }}</small>
              </h4>
            </div>
            <b-avatar
              :variant="kpi.variant"
              size="38px"
            >
              <feather-icon
                :icon="kpi.icon"
                size="18"
              />
            </b-avatar>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        cols="12"
        xl="7"
        class="mb-1"
      >
        <b-card
          no-body
          class="shadow-sm border-0 journey-map-card"
          :class="{ 'is-fullscreen': isMapFullscreen }"
        >
          <div class="journey-map-header">
            <div class="min-width-0">
              <h5 class="mb-0 font-weight-bolder">
                {{ $t('Location') }}
              </h5>
              <small class="text-muted">
                <template v-if="stops.length">
                  {{ stops.length }} {{ $t('Stops') }}
                </template>
                <template v-else-if="isJourneyLoading">
                  {{ $t('Loading') }}
                </template>
                <template v-else>
                  {{ $t('no data ...') }}
                </template>
              </small>
            </div>
            <div
              class="d-flex align-items-center flex-wrap"
              style="gap: 8px;"
            >
              <div class="d-none d-lg-flex align-items-center font-small-2 text-muted journey-legend">
                <span class="legend-dot legend-start" />
                {{ $t('Start') }}
                <span class="legend-dot legend-stop" />
                {{ $t('Stops') }}
                <span class="legend-dot legend-end" />
                {{ $t('End') }}
                <span class="legend-cluster">+</span>
                {{ $t('Grouped') }}
              </div>
              <b-button-group size="sm">
                <b-button
                  :variant="btnMap ? 'primary' : 'outline-primary'"
                  @click="chooseLayer('map')"
                >
                  {{ $t('Map') }}
                </b-button>
                <b-button
                  :variant="btnGround ? 'primary' : 'outline-primary'"
                  @click="chooseLayer('ground')"
                >
                  {{ $t('Ground') }}
                </b-button>
              </b-button-group>
              <b-button
                v-b-tooltip.hover
                size="sm"
                :variant="isMapFullscreen ? 'primary' : 'outline-primary'"
                class="d-inline-flex align-items-center flex-shrink-0"
                :title="isMapFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
                @click="toggleMapFullscreen"
              >
                <feather-icon
                  :icon="isMapFullscreen ? 'MinimizeIcon' : 'MaximizeIcon'"
                  size="14"
                  class="mr-50"
                />
                <span class="d-none d-sm-inline">
                  {{ isMapFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen') }}
                </span>
              </b-button>
            </div>
          </div>

          <div class="journey-map-stage">
            <div
              v-if="isJourneyLoading"
              class="journey-map-status"
            >
              <b-spinner
                small
                variant="primary"
              />
              <span>{{ $t('Loading') }}</span>
            </div>
            <div
              v-if="!isJourneyLoading && !routeLine.length"
              class="journey-map-empty"
            >
              <h5 class="font-weight-bolder mb-25">
                {{ $t('no data ...') }}
              </h5>
              <p class="text-muted font-small-3 mb-0">
                {{ $t('Apply Filters') }}
              </p>
            </div>

            <l-map
              ref="myMapJourney"
              class="journey-map"
              :zoom="zoom"
              :center="center"
            >
              <l-tile-layer :url="mapUrl" />
              <l-polyline
                :lat-lngs="routeLine"
                color="#ffffff"
                :weight="8"
                :opacity="0.92"
              />
              <l-polyline
                :lat-lngs="routeLine"
                color="#7367f0"
                :weight="4"
                :opacity="0.95"
              />
              <marker-cluster
                v-if="clusteredStops.length"
                :options="clusterOptions"
              >
                <l-marker
                  v-for="item in clusteredStops"
                  :key="'journey-pin-' + item.index"
                  :lat-lng="[Number(item.stop.lat), Number(item.stop.lng)]"
                  :icon="stopIcon(item.index)"
                  @click="focusStop(item.index)"
                >
                  <l-tooltip :options="markerTooltipOptions">
                    <div class="font-small-2">
                      <div class="font-weight-bolder mb-25">
                        #{{ item.index + 1 }}
                      </div>
                      <div v-if="item.stop.arrive">
                        {{ $t('Arrival') }}: <strong>{{ formatClock(item.stop.arrive) }}</strong>
                      </div>
                      <div v-if="item.stop.departure">
                        {{ $t('Departure') }}: <strong>{{ formatClock(item.stop.departure) }}</strong>
                      </div>
                      <div v-if="stayLabel(item.stop)">
                        {{ $t('Permanence') }}: <strong>{{ stayLabel(item.stop) }}</strong>
                      </div>
                    </div>
                  </l-tooltip>
                </l-marker>
              </marker-cluster>
              <l-marker
                v-for="item in anchorStops"
                :key="'journey-anchor-' + item.index"
                :lat-lng="[Number(item.stop.lat), Number(item.stop.lng)]"
                :icon="stopIcon(item.index)"
                :z-index-offset="item.index === selectedStopIndex ? 2000 : 1400"
                @click="focusStop(item.index)"
              >
                <l-tooltip :options="markerTooltipOptions">
                  <div class="font-small-2">
                    <div class="font-weight-bolder mb-25">
                      #{{ item.index + 1 }}
                    </div>
                    <div v-if="item.stop.arrive">
                      {{ $t('Arrival') }}: <strong>{{ formatClock(item.stop.arrive) }}</strong>
                    </div>
                    <div v-if="item.stop.departure">
                      {{ $t('Departure') }}: <strong>{{ formatClock(item.stop.departure) }}</strong>
                    </div>
                    <div v-if="stayLabel(item.stop)">
                      {{ $t('Permanence') }}: <strong>{{ stayLabel(item.stop) }}</strong>
                    </div>
                  </div>
                </l-tooltip>
              </l-marker>
            </l-map>
            <div
              v-if="clusteredStops.length"
              class="journey-map-hint"
            >
              {{ $t('Nearby points are grouped. Zoom in or click a group to separate them.') }}
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        xl="5"
        class="mb-1"
      >
        <b-card
          no-body
          class="shadow-sm border-0 journey-side-card h-100"
        >
          <b-tabs
            pills
            class="journey-tabs"
            nav-class="px-1 pt-1"
          >
            <b-tab active>
              <template #title>
                <feather-icon
                  icon="OctagonIcon"
                  size="16"
                  class="mr-50"
                />
                <span>{{ $t('Stops') }}</span>
                <b-badge
                  v-if="stops.length"
                  variant="light-primary"
                  class="ml-50"
                >
                  {{ stops.length }}
                </b-badge>
              </template>
              <div class="journey-scroll">
                <div
                  v-if="isJourneyLoading"
                  class="text-center py-3"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!stops.length"
                  class="text-center py-3"
                >
                  <h5 class="font-weight-bolder mb-25">
                    {{ $t('no data ...') }}
                  </h5>
                  <p class="text-muted font-small-3 mb-0">
                    {{ $t('Apply Filters') }}
                  </p>
                </div>
                <button
                  v-for="(stop, index) in stops"
                  :key="'stop-row-' + index"
                  type="button"
                  class="journey-stop"
                  :class="{ active: selectedStopIndex === index }"
                  @click="focusStop(index)"
                >
                  <span
                    class="journey-stop-index"
                    :class="{
                      'is-start': index === 0,
                      'is-end': stops.length > 1 && index === stops.length - 1,
                    }"
                  >
                    {{ index + 1 }}
                  </span>
                  <div class="min-width-0 flex-grow-1">
                    <div class="d-flex align-items-center justify-content-between mb-25">
                      <strong>{{ stopHeadline(index, stop) }}</strong>
                      <b-badge
                        v-if="stayLabel(stop)"
                        variant="light-warning"
                      >
                        {{ stayLabel(stop) }}
                      </b-badge>
                    </div>
                    <div class="font-small-2 journey-address">
                      {{ pointLabel(stop) }}
                    </div>
                  </div>
                </button>
              </div>
            </b-tab>

            <b-tab>
              <template #title>
                <feather-icon
                  icon="NavigationIcon"
                  size="16"
                  class="mr-50"
                />
                <span>{{ $t('Ride') }}</span>
                <b-badge
                  v-if="rides.length"
                  variant="light-primary"
                  class="ml-50"
                >
                  {{ rides.length }}
                </b-badge>
              </template>
              <div class="journey-scroll">
                <div
                  v-if="isJourneyLoading"
                  class="text-center py-3"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!rides.length"
                  class="text-center py-3"
                >
                  <h5 class="font-weight-bolder mb-25">
                    {{ $t('no data ...') }}
                  </h5>
                  <p class="text-muted font-small-3 mb-0">
                    {{ $t('Apply Filters') }}
                  </p>
                </div>
                <div
                  v-for="(ride, index) in rides"
                  :key="'ride-' + index"
                  class="journey-ride"
                >
                  <button
                    type="button"
                    class="journey-ride-point"
                    @click="focusCoordinate(ride.origin && ride.origin.lat, ride.origin && ride.origin.lng)"
                  >
                    <span class="journey-stop-index is-start">{{ index + 1 }}</span>
                    <div class="min-width-0 text-left">
                      <strong>{{ formatClock(ride.origin && ride.origin.departure) }}</strong>
                      <div class="font-small-2 journey-address">
                        {{ pointLabel(ride.origin) }}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    class="journey-ride-point"
                    @click="focusCoordinate(ride.destination && ride.destination.lat, ride.destination && ride.destination.lng)"
                  >
                    <span class="journey-stop-index is-end">{{ index + 1 }}</span>
                    <div class="min-width-0 text-left">
                      <strong>{{ formatClock(ride.destination && ride.destination.arrive) }}</strong>
                      <div class="font-small-2 journey-address">
                        {{ pointLabel(ride.destination) }}
                      </div>
                    </div>
                  </button>
                  <div
                    class="d-flex flex-wrap mt-50"
                    style="gap: 6px;"
                  >
                    <b-badge variant="light-primary">
                      {{ ride.distanceInMeters }} Km
                    </b-badge>
                    <b-badge
                      v-if="ride.time"
                      variant="light-secondary"
                    >
                      {{ String(ride.time).substring(0, 5) }}
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>
    <!--Modal Create Export-->
    <b-modal
      ref="modal-export"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
    >
      <div class="mx-3 text-center">
        <h3>{{ $t('Preparing report') }}...</h3>
        <b-spinner
          variant="primary"
          :label="$t('Loading')"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BForm,
  BInputGroup,
  BInputGroupPrepend,
  BModal,
  BRow,
  BSpinner,
  BTab,
  BTabs,
  VBTooltip,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import {
  LMap, LMarker, LPolyline, LTileLayer, LTooltip,
} from 'vue2-leaflet'
import MarkerCluster from 'vue2-leaflet-markercluster'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-polylinedecorator'
import markerIconUrl from 'leaflet/dist/images/marker-icon.png'
import markerIconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png'
import store from '@/store'
import { ref } from '@vue/composition-api'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'
import queriesStoreModule from '../../queriesStoreModule'

// O webpack não resolve o caminho relativo do ícone padrão do Leaflet.
// eslint-disable-next-line no-underscore-dangle
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIconRetinaUrl,
  shadowUrl: markerShadowUrl,
})

const JOURNEY_ICON_CACHE = {}
const POLYLINE_COLOR = '#7367f0'
const MAX_HISTORY_PAGES = 400

function journeyClusterRadius(zoom) {
  if (zoom >= 18) return 22
  if (zoom >= 16) return 34
  if (zoom >= 14) return 48
  return 68
}

function journeyClusterTier(count) {
  if (count >= 20) return 'lg'
  if (count >= 8) return 'md'
  return 'sm'
}

function journeyClusterSize(tier) {
  if (tier === 'lg') return 46
  if (tier === 'md') return 38
  return 32
}

function createJourneyClusterIcon(cluster) {
  const count = cluster.getChildCount()
  const tier = journeyClusterTier(count)
  const size = journeyClusterSize(tier)
  return L.divIcon({
    className: `journey-cluster journey-cluster-${tier}`,
    html: `<span>${count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

export default {
  components: {
    BAvatar,
    BBadge,
    BButton,
    BButtonGroup,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BForm,
    BInputGroup,
    BInputGroupPrepend,
    BModal,
    BRow,
    BSpinner,
    BTab,
    BTabs,
    LMap,
    LMarker,
    LPolyline,
    LTileLayer,
    LTooltip,
    MarkerCluster,
    ValidationObserver,
    ValidationProvider,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    myId: {
      type: String,
      required: true,
    },

  },
  data() {
    return {
      btnGround: false,
      btnMap: true,
      btnHybrid: false,

      // Rules
      required,

      // Map
      mapUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      zoom: 4,
      center: [-10.1500034, -67.9163393],

      journeyData: {},
      selectedStopIndex: null,
      isMapFullscreen: false,
      clusterOptions: {
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        spiderfyOnMaxZoom: true,
        spiderfyDistanceMultiplier: 1.5,
        maxClusterRadius: journeyClusterRadius,
        iconCreateFunction: createJourneyClusterIcon,
        spiderLegPolylineOptions: {
          weight: 1.5,
          color: POLYLINE_COLOR,
          opacity: 0.55,
        },
      },
      markerTooltipOptions: {
        direction: 'top',
        offset: [0, -8],
        opacity: 0.97,
      },
      routeData: [],
      loadingJourney: false,
      loadingHistory: false,

      latlngs: [],
      exportPdf: false,

      flatpickrDateConfig: {
        enableTime: false,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd/m/Y',
        altInputClass: 'form-control',
        locale: getFlatpickrLocale(),
        allowInput: true,
      },
      flatpickrConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
        locale: getFlatpickrLocale(),
        allowInput: true,
      },
      startHour: '',
      endHour: '',
    }
  },
  computed: {
    stops() {
      return Array.isArray(this.journeyData.stops) ? this.journeyData.stops : []
    },
    rides() {
      return Array.isArray(this.journeyData.rides) ? this.journeyData.rides : []
    },
    isJourneyLoading() {
      return this.loadingJourney || this.loadingHistory
    },
    plottedStops() {
      return this.stops
        .map((stop, index) => ({ stop, index }))
        .filter(item => this.coordinateIsValid(item.stop.lat, item.stop.lng))
    },
    anchorStops() {
      return this.plottedStops.filter(item => this.isAnchorIndex(item.index))
    },
    clusteredStops() {
      return this.plottedStops.filter(item => !this.isAnchorIndex(item.index))
    },
    routeLine() {
      if (this.latlngs.length) return this.latlngs
      return this.plottedStops.map(item => [Number(item.stop.lat), Number(item.stop.lng)])
    },
    journeyKpis() {
      return [
        {
          key: 'stops',
          label: this.$t('Stops'),
          value: this.stops.length ? String(this.stops.length) : '—',
          unit: '',
          icon: 'OctagonIcon',
          variant: 'light-primary',
          valueClass: 'text-primary',
        },
        {
          key: 'rides',
          label: this.$t('Ride'),
          value: this.rides.length ? String(this.rides.length) : '—',
          unit: '',
          icon: 'NavigationIcon',
          variant: 'light-info',
          valueClass: 'text-info',
        },
        {
          key: 'distance',
          label: this.$t('Travelled Distance'),
          value: this.totalDistanceLabel,
          unit: this.rides.length ? 'Km' : '',
          icon: 'FlagIcon',
          variant: 'light-success',
          valueClass: 'text-success',
        },
        {
          key: 'permanence',
          label: this.$t('Permanence'),
          value: this.totalStayLabel,
          unit: '',
          icon: 'ClockIcon',
          variant: 'light-warning',
          valueClass: 'text-warning',
        },
      ]
    },
    totalDistanceLabel() {
      if (!this.rides.length) return '—'
      const total = this.rides.reduce((sum, ride) => sum + (Number(ride.distanceKm) || 0), 0)
      return total.toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    totalStayLabel() {
      const seconds = this.stops.reduce((sum, stop) => sum + this.durationToSeconds(stop.stayTime), 0)
      if (!seconds) return '—'
      return this.formatDuration(seconds)
    },
  },
  mounted() {
    const QUERIES_APP_STORE_MODULE_NAME = 'app-queries'
    if (!store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.registerModule(QUERIES_APP_STORE_MODULE_NAME, queriesStoreModule)
    this.onJourneyKeydown = event => {
      if (event.key === 'Escape' && this.isMapFullscreen) this.toggleMapFullscreen()
    }
    document.addEventListener('keydown', this.onJourneyKeydown)
    try {
      if (localStorage.layerPreview === 'ground') this.chooseLayer('ground')
    } catch (e) {
      // armazenamento local indisponível
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onJourneyKeydown)
    document.body.style.overflow = ''
    this.clearRouteDecorator()
  },
  methods: {
    coordinateIsValid(latitude, longitude) {
      const lat = parseFloat(latitude)
      const lng = parseFloat(longitude)
      return !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0 && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
    },

    isAnchorIndex(index) {
      const last = this.stops.length - 1
      return index === 0 || index === last || index === this.selectedStopIndex
    },

    stopIcon(index) {
      let variant = 'stop'
      let size = 22
      if (index === this.selectedStopIndex) {
        variant = 'selected'
        size = 30
      } else if (index === 0) {
        variant = 'start'
        size = 26
      } else if (this.stops.length > 1 && index === this.stops.length - 1) {
        variant = 'end'
        size = 26
      }
      const label = String(index + 1)
      const cacheKey = `${variant}-${size}-${label}`
      if (!JOURNEY_ICON_CACHE[cacheKey]) {
        JOURNEY_ICON_CACHE[cacheKey] = L.divIcon({
          className: `journey-pin journey-pin-${variant}`,
          html: `<span>${label}</span>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        })
      }
      return JOURNEY_ICON_CACHE[cacheKey]
    },

    formatClock(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    stayLabel(stop) {
      if (!stop || !stop.stayTime || stop.stayTime === '00:00:00') return ''
      return String(stop.stayTime).substring(0, 5)
    },

    durationToSeconds(value) {
      if (!value || value === '00:00:00') return 0
      const [hours, minutes, seconds] = String(value).split(':').map(part => Number(part) || 0)
      return (hours * 3600) + (minutes * 60) + seconds
    },

    formatDuration(totalSeconds) {
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },

    pointLabel(point) {
      if (!point) return this.$t('no data ...')
      if (point.addressComplete) return point.addressComplete
      if (!this.coordinateIsValid(point.lat, point.lng)) return this.$t('no data ...')
      return `${Number(point.lat).toFixed(5)}, ${Number(point.lng).toFixed(5)}`
    },

    stopHeadline(index, stop) {
      if (index === 0) return `${this.$t('Start')} · ${this.formatClock(stop.departure || stop.arrive)}`
      if (this.stops.length > 1 && index === this.stops.length - 1) {
        return `${this.$t('End')} · ${this.formatClock(stop.arrive || stop.departure)}`
      }
      const arrive = this.formatClock(stop.arrive)
      const departure = this.formatClock(stop.departure)
      if (arrive && departure) return `${arrive} – ${departure}`
      return arrive || departure || `#${index + 1}`
    },

    clearRouteDecorator() {
      if (!this.journeyDecorator) return
      try {
        this.journeyDecorator.remove()
      } catch (e) {
        // o mapa pode já ter sido destruído
      }
      this.journeyDecorator = null
    },

    simplifyLine(points, maxPoints = 250) {
      if (points.length <= maxPoints) return points
      const step = Math.ceil(points.length / maxPoints)
      const simplified = points.filter((_, index) => index % step === 0)
      const last = points[points.length - 1]
      if (simplified[simplified.length - 1] !== last) simplified.push(last)
      return simplified
    },

    drawRouteDecorator() {
      this.clearRouteDecorator()
      const map = this.$refs.myMapJourney && this.$refs.myMapJourney.mapObject
      if (!map || !L.polylineDecorator || !L.Symbol || this.latlngs.length < 2) return
      const line = L.polyline(this.simplifyLine(this.latlngs))
      this.journeyDecorator = L.polylineDecorator(line, {
        patterns: [{
          offset: 12,
          repeat: 90,
          symbol: L.Symbol.arrowHead({
            pixelSize: 10,
            pathOptions: { fillOpacity: 1, weight: 0, color: POLYLINE_COLOR },
          }),
        }],
      }).addTo(map)
    },

    fitJourneyMap() {
      const map = this.$refs.myMapJourney && this.$refs.myMapJourney.mapObject
      const points = this.routeLine
      if (!map) return
      map.invalidateSize()
      if (!points.length) return
      try {
        map.fitBounds(points, { padding: [40, 40] })
      } catch (e) {
        // bounds inválidos
      }
      this.drawRouteDecorator()
    },

    focusStop(index) {
      const stop = this.stops[index]
      if (!stop) return
      this.selectedStopIndex = index
      this.$nextTick(() => {
        this.focusCoordinate(stop.lat, stop.lng, false)
        const activeStop = this.$el.querySelector('.journey-stop.active')
        if (activeStop && typeof activeStop.scrollIntoView === 'function') {
          activeStop.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },

    focusCoordinate(latitude, longitude, selectNearest = true) {
      if (!this.coordinateIsValid(latitude, longitude)) return
      if (selectNearest) {
        const match = this.stops.findIndex(stop => Number(stop.lat) === Number(latitude) && Number(stop.lng) === Number(longitude))
        if (match >= 0) this.selectedStopIndex = match
      }
      this.$nextTick(() => {
        const map = this.$refs.myMapJourney && this.$refs.myMapJourney.mapObject
        if (!map) return
        const nextZoom = Math.max(map.getZoom(), 17)
        map.flyTo([Number(latitude), Number(longitude)], nextZoom, { duration: 0.45 })
      })
    },

    toggleMapFullscreen() {
      this.isMapFullscreen = !this.isMapFullscreen
      document.body.style.overflow = this.isMapFullscreen ? 'hidden' : ''
      setTimeout(() => this.fitJourneyMap(), 60)
    },

    chooseLayer(url) {
      try {
        localStorage.layerPreview = url
      } catch (e) {
        // armazenamento local indisponível
      }

      if (url === 'ground') {
        this.btnGround = true
        this.btnMap = false
        this.btnHybrid = false
        this.mapUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      }
      if (url === 'map') {
        this.btnGround = false
        this.btnMap = true
        this.btnHybrid = false
        this.mapUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      }
      if (url === 'hybrid') {
        this.btnGround = false
        this.btnMap = false
        this.btnHybrid = true
        this.mapUrl = 'https://2.aerial.maps.ls.hereapi.com/maptile/2.1/maptile/newest/hybrid.day/{z}/{x}/{y}/256/png8?lg=por&apiKey=U7wvT2IU0rFTeGwMoIpdf7JaQx5QH74SyhG4qiHy7Q8'
      }
      // else this.url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    },

    showModalExport() {
      this.$refs['modal-export'].show()
    },
    hideModalExport() {
      this.$refs['modal-export'].hide()
    },

    async exportJourneyToPDF() {
      this.exportPdf = true
      this.showModalExport()
      await store.dispatch('app-queries/exportJourneyToPDF', {
        id: this.myId,
        date: this.date,

      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `journey_${this.date}.pdf`)
        document.body.appendChild(link)
        link.click()

        this.exportPdf = false
        this.hideModalExport()
      })
    },

    validationForm() {
      this.$refs.simpleRules.validate().then(async success => {
        if (success) {
          await this.fetchRouteHistory()
          await this.fetchJourney()
        }
      })
    },

    async fetchJourney() {
      this.journeyData = {
        rides: [],
        stops: [],
      }
      this.selectedStopIndex = null
      this.loadingJourney = true

      const res = await store.dispatch('app-queries/fetchJourney', {
        id: this.myId,
        date: this.date,
        startHour: this.startHour,
        endHour: this.endHour,
      }).catch(() => {
        this.showSnackbar({
          title: this.$t('An unexpected error occurred'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        })
        return null
      })

      if (res && res.data) {
        if (Array.isArray(res.data.rides)) {
          res.data.rides.forEach(ride => {
            const distanceKm = parseFloat((Number(ride.distanceInMeters) / 1000).toFixed(2))
            this.journeyData.rides.push({
              ...ride,
              distanceKm,
              distanceInMeters: distanceKm.toLocaleString(),
            })
          })
        }
        if (Array.isArray(res.data.stops)) {
          res.data.stops.forEach(stop => {
            this.journeyData.stops.push(stop)
          })
        }
      }
      this.loadingJourney = false
      this.$nextTick(() => this.fitJourneyMap())
    },

    showSnackbar(config = { title: '', icon: '', variant: 'info' }) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: config.title,
          icon: config.icon,
          variant: config.variant,
        },
      })
    },

    async fetchRouteHistory() {
      let finished = false
      let page = 1
      this.routeData = []
      this.latlngs = []
      this.loadingHistory = true
      this.clearRouteDecorator()

      try {
        while (!finished && page <= MAX_HISTORY_PAGES) {
          // eslint-disable-next-line no-await-in-loop
          const res = await store.dispatch('app-queries/fetchRouteHistory', {
            id: this.myId,
            startDate: this.date,
            endDate: this.date,
            startHour: this.startHour,
            endHour: this.endHour,
            page,
          }).catch(() => {
            this.showSnackbar({
              title: this.$t('An unexpected error occurred'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            })
            this.routeData = []
            this.latlngs = []
            return null
          })

          const points = res && res.data && Array.isArray(res.data.data) ? res.data.data : []
          if (!points.length) {
            finished = true
            break
          }
          points.forEach(point => {
            if (!this.coordinateIsValid(point.lat, point.lng)) return
            this.routeData.push(point)
            this.latlngs.push([Number(point.lat), Number(point.lng)])
          })
          page += 1
        }
      } finally {
        this.loadingHistory = false
        this.$nextTick(() => this.fitJourneyMap())
      }
    },
  },
  setup() {
    const date = ref('')
    const startTime = ref('')
    const endTime = ref('')

    return {
      date,
      startTime,
      endTime,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/map-leaflet.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '~leaflet.markercluster/dist/MarkerCluster.css';

.card-journey {

  height: 80vh !important;
}

.no-icon {
  width: 17px;
}

.card-telemetry {
  border-top: 3px solid #00cfe8;
  -webkit-box-shadow: 0 4px 24px 0 rgb(34 41 47 / 10%);
  box-shadow: 0 4px 24px 0 rgb(34 41 47 / 10%);
  border-radius: .428rem;

}

.text-index {
  margin: auto;
  color: white;
  text-shadow: -1px 2px black;
}

.div-img {
  background: #f8f9fa;
  border-radius: 0px 0px 0px 0.428rem;
  border-right: 1px solid #82868b;
}

.index {
  background-color: #00cfe8;
  border-radius: 50%;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  width: 2.7rem;
  padding-top: 0.3rem;
  padding-bottom: 0.3rem;
  max-width: 40px;
  display: flex;
  color: white;
  width: 2rem;
  max-width: 40px;

}

.journey-pin,
.journey-cluster {
  display: flex !important;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 50%;
  background-image: none !important;
  color: #fff;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}

.journey-pin {
  border: 2px solid #fff;
  background: #7367f0;
  font-size: 11px;
  box-shadow: 0 1px 4px rgba(34, 41, 47, 0.35);

  span {
    pointer-events: none;
  }
}

.journey-pin-start {
  background: #28c76f;
}

.journey-pin-end {
  background: #ea5455;
}

.journey-pin-selected {
  background: #ff9f43;
  box-shadow: 0 0 0 5px rgba(255, 159, 67, 0.35);
}

.journey-cluster {
  border: 3px solid #fff;
  background: rgba(115, 103, 240, 0.94);
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.28);
  font-size: 12px;

  span {
    pointer-events: none;
  }
}

.journey-cluster-md {
  font-size: 13px;
}

.journey-cluster-lg {
  font-size: 14px;
}

body.dark-layout .journey-report {
  .journey-map-header,
  .journey-ride {
    border-color: rgba(255, 255, 255, 0.08);
  }

  .journey-map-card.is-fullscreen,
  .journey-map-status,
  .journey-map-hint,
  .journey-map-empty,
  .journey-stop {
    background: #283046;
    color: #d0d2d6;
  }

  .journey-stop,
  .journey-ride {
    border-color: rgba(255, 255, 255, 0.08);
  }

  .journey-address {
    color: #b4b7bd;
  }
}

.pinStart {
  background-color: #03dac5 !important;
  /*padding: 10px;*/
  border-radius: 50%;
  /*box-shadow: 5px 3px 10px rgba(0, 0, 0, 0.2);*/
  text-align: center;
  width: auto !important;
  height: auto !important;
}

.asset-on {
  background-color: #28c76f !important;
}

.asset-off {
  background-color: #82868b !important;
}

.asset-failure {
  background-color: #ef5258 !important;
}

.asset-noconnection {
  background-color: #f8f9fa !important;
}

.asset-severe {
  background-color: #ff9d54 !important;
}

.asset-iddle {
  background-color: #00cfe8 !important;
}

.with-icon {
  width: 40px;
}

.vue2leaflet-map {
  &.leaflet-container {
    height: 70.5vh !important;
  }
}

.journey-report {
  .filters-card,
  .journey-map-card,
  .journey-side-card,
  .journey-kpi {
    border-radius: 0.5rem;
  }

  .journey-kpi-unit {
    font-size: 0.8rem;
    font-weight: 500;
    color: #b9b9c3;
  }

  .journey-map-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid #ebe9f1;
  }

  .journey-legend {
    gap: 6px;
  }

  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    margin-left: 8px;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px rgba(34, 41, 47, 0.08);

    &.legend-start {
      background: #28c76f;
    }

    &.legend-stop {
      background: #7367f0;
    }

    &.legend-end {
      background: #ea5455;
    }
  }

  .legend-cluster {
    min-width: 18px;
    height: 18px;
    margin-left: 8px;
    padding: 0 4px;
    border-radius: 999px;
    background: #7367f0;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .journey-map-stage {
    position: relative;
    min-height: 420px;
  }

  .journey-map-status,
  .journey-map-hint {
    position: absolute;
    z-index: 500;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 4px 16px rgba(34, 41, 47, 0.14);
    color: #5e5873;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .journey-map-status {
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0.4rem 0.75rem;
    border-radius: 999px;
  }

  .journey-map-hint {
    left: 12px;
    bottom: 18px;
    max-width: min(280px, calc(100% - 24px));
    padding: 0.4rem 0.7rem;
    line-height: 1.35;
    pointer-events: none;
  }

  .journey-map-empty {
    position: absolute;
    inset: 0;
    z-index: 450;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.88);
  }

  .journey-map.leaflet-container {
    height: 62vh !important;
    min-height: 420px;
    width: 100%;
    border-radius: 0 0 0.5rem 0.5rem;
    z-index: 1;
  }

  .journey-map-card.is-fullscreen {
    position: fixed !important;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 9999;
    width: 100vw;
    height: 100vh;
    margin: 0 !important;
    border-radius: 0 !important;
    display: flex;
    flex-direction: column;
    background: #fff;

    .journey-map-stage {
      flex: 1;
      min-height: 0;
    }

    .journey-map.leaflet-container {
      position: absolute;
      inset: 0;
      height: 100% !important;
      min-height: 0;
      border-radius: 0;
    }
  }

  .journey-scroll {
    max-height: 62vh;
    overflow-y: auto;
    padding: 0.75rem 1rem 1rem;
  }

  .journey-stop,
  .journey-ride-point {
    width: 100%;
    display: flex;
    gap: 0.75rem;
    text-align: left;
    border: 1px solid #ebe9f1;
    background: #fff;
    border-radius: 0.5rem;
    padding: 0.7rem 0.75rem;
    margin-bottom: 0.5rem;
  }

  .journey-stop {
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover,
    &.active {
      border-color: #7367f0;
      box-shadow: 0 2px 8px rgba(115, 103, 240, 0.12);
    }
  }

  .journey-stop-index {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #7367f0;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;

    &.is-start {
      background: #28c76f;
    }

    &.is-end {
      background: #ea5455;
    }
  }

  .journey-address {
    color: #5e5873;
    line-height: 1.35;
  }

  .journey-ride {
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .journey-ride-point {
    border: 0;
    padding: 0.35rem 0;
    margin-bottom: 0.25rem;
    background: transparent;
  }

  .min-width-0 {
    min-width: 0;
  }
}

.text-header {
  //font-size: clamp(0.5em, 0.7em + 0.2vw, 5.5em);
  font-size: 1em;
}

//@media
/* @media screen and (min-height: 768px) {
    .vue2leaflet-map.leaflet-container {
        height: 54vh !important;
    }

    .card-journey {
        height: 64vh !important;
    }
}

@media screen and (min-height: 900px) {
    .vue2leaflet-map.leaflet-container {
        height: 60vh !important;
    }

    .card-journey {
        height: 68.6vh !important;
    }
}

@media screen and (min-height: 1024px) {
    .vue2leaflet-map.leaflet-container {
        height: 66vh !important;
    }

    .card-journey {
        height: 74vh !important;
    }
} */
</style>
<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
</style>
